import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Redirect, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import ContainerLayout from 'components/container-layout';
import {
  buildChildSitePath,
  buildSignInPath,
  buildSiteInvitationPath,
  buildSitePendingExpertApprovalPath,
  CONFIRMATION_SIGN_UP_PATH,
  NOT_FOUND_PATH,
  PENDING_EXPERT_APPROVAL_PATH,
  RESET_PASSWORD_PATH,
  ROOT_PATH,
  SIGN_IN_PATH,
  SITE_INVITATION_PATH,
} from 'constants/route-paths';
import { mfaChallengeSelector } from 'selectors/auth';
import { useAuthenticated, useDetectingAuthenticatedUser, useHandleSignIn, useSignedInUser } from 'hooks/auth';
import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import userActions from 'actions/user-actions';
import sidebarMenuActions from 'actions/sidebar-menu-actions';
import { useGetCurrentUserQuery } from 'hooks/current-user';
import auth0Actions from 'actions/auth0-actions';
import SkeletonLayout from 'components/skeleton-layout';
import { mfaChallengeForSite } from 'utils/mfa';

const RouteValidation = ({
  container,
  privateRoute = false,
  lockable = false,
  hideDetectingAuthenticatedUser = false,
  expertOnly = false,
  ...props
}) => {
  const { siteSlug } = useParams();

  const dispatch = useDispatch();
  const location = useLocation();

  const { path: routeMatch } = useRouteMatch();

  const {
    siteId,
    locked: siteLocked,
    sidebarMenu,
    enabledSsoFeature,
    loading,
  } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);
  const isAuthenticated = !!useAuthenticated();
  const mfaChallenge = mfaChallengeForSite(useSelector(mfaChallengeSelector), siteId);
  const { isMember, isExpert, pendingExpertApproval, suspendedExpert } = useSignedInUser();

  const [loadingUser, setLoadingUser] = useState(true);

  useHandleSignIn();
  useDetectingAuthenticatedUser(!hideDetectingAuthenticatedUser);
  useGetCurrentUserQuery();

  useEffect(() => {
    dispatch(auth0Actions.signInWithAuth0User({ disabled: !enabledSsoFeature || hideDetectingAuthenticatedUser }));
  }, [dispatch, enabledSsoFeature, hideDetectingAuthenticatedUser]);

  useEffect(() => {
    dispatch(sidebarMenuActions.setSidebarMenu(sidebarMenu));
  }, [dispatch, sidebarMenu]);

  useEffect(() => {
    if (isAuthenticated && siteSlug && siteId) {
      setLoadingUser(privateRoute || expertOnly);

      dispatch(userActions.getSpecificForSite(siteId)).then(() => setLoadingUser(false));
    } else {
      setLoadingUser(false);
    }
  }, [dispatch, expertOnly, isAuthenticated, privateRoute, siteId, siteSlug]);

  // A pending challenge is answered on the sign-in page, which then resumes at `from`. One from a
  // sign-in flow always goes there (no session yet); a 403 step-up only when the route needs a
  // session — public pages keep rendering, with the site banner. One-shot callbacks can't be
  // replayed, so they resume at the site.
  const mfaChallengeRedirect = useMemo(() => {
    if (!mfaChallenge || routeMatch === SIGN_IN_PATH) return null;
    if (mfaChallenge.stepUp && !privateRoute) return null;

    const oneShot = routeMatch === CONFIRMATION_SIGN_UP_PATH || routeMatch === RESET_PASSWORD_PATH;
    const from = oneShot ? (siteSlug ? buildChildSitePath(siteSlug) : ROOT_PATH) : location;

    return { pathname: buildSignInPath(siteSlug), state: { from } };
  }, [mfaChallenge, location, privateRoute, routeMatch, siteSlug]);

  const privateRouteFallback = useMemo(() => {
    if (!isAuthenticated && (privateRoute || (siteLocked && lockable))) {
      toast.error('You have to sign in before perform this page', { toastId: 'privateRouteError' });

      return {
        pathname: buildSignInPath(siteSlug),
        state: {
          from: location,
        },
      };
    }
  }, [isAuthenticated, location, lockable, privateRoute, siteLocked, siteSlug]);

  const userRedirectState = useMemo(() => {
    if (!siteSlug || loadingUser) return null;

    if (privateRoute && !isMember && routeMatch !== SITE_INVITATION_PATH) {
      return {
        pathname: buildSiteInvitationPath(siteSlug),
        state: {
          from: location,
          requireUserCode: true,
        },
      };
    }

    if (isExpert && pendingExpertApproval && routeMatch !== PENDING_EXPERT_APPROVAL_PATH) {
      return {
        pathname: buildSitePendingExpertApprovalPath(siteSlug),
        state: {
          from: location,
        },
      };
    }

    if (suspendedExpert && expertOnly) {
      return buildChildSitePath(siteSlug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMatch, isAuthenticated, loadingUser, lockable, privateRoute, siteLocked, siteSlug]);

  if (!loading && !siteId) {
    return <Redirect to={NOT_FOUND_PATH} />;
  }

  if (mfaChallengeRedirect) {
    return <Redirect to={mfaChallengeRedirect} />;
  }

  if (privateRouteFallback) {
    return <Redirect to={privateRouteFallback} />;
  }

  if (loadingUser) {
    return <SkeletonLayout />;
  }

  if (userRedirectState) {
    return <Redirect to={userRedirectState} />;
  }

  return (
    <React.Suspense fallback={null}>
      <ContainerLayout container={container} {...props} />
    </React.Suspense>
  );
};

RouteValidation.propTypes = {
  container: PropTypes.elementType.isRequired,
  privateRoute: PropTypes.bool,
  lockable: PropTypes.bool,
  hideDetectingAuthenticatedUser: PropTypes.bool,
  expertOnly: PropTypes.bool,
  customPageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  staticJsonUrl: PropTypes.string,
};

export default RouteValidation;
